import { Button } from '@/components/ui/button'
import { AppTooltip } from '@components/AppTooltip'
import { type Component, type JSXElement, Show } from 'solid-js'
import { css } from 'styled-system/css'
import { Box } from 'styled-system/jsx'
import { promptTooltipClass } from '../../../promptTooltipClass'

interface ActionButtonProps {
  heading: string
  tooltip: string
  icon: JSXElement
  active?: boolean
  disabled?: boolean
  loading?: boolean
  bubble?: string
  onClick: () => void
  size?: 'main' | 'admin' | 'mobile' | 'mobileAdmin'
  danger?: boolean
}

const SIZES = {
  main: { h: '39px', borderRadius: '10px', fontSize: '12px', gap: '6px', icon: '14px', badge: '20px', badgeOffset: '-7px' },
  admin: { h: '31px', borderRadius: '8px', fontSize: '11px', gap: '5px', icon: '13px', badge: '20px', badgeOffset: '-7px' },
  mobile: { h: '42px', borderRadius: '11px', fontSize: '12.5px', gap: '5px', icon: '15px', badge: '19px', badgeOffset: '-6px' },
  mobileAdmin: { h: '34px', borderRadius: '9px', fontSize: '11.5px', gap: '5px', icon: '13px', badge: '19px', badgeOffset: '-6px' },
} as const

export const ActionButton: Component<ActionButtonProps> = (props) => {
  const scale = () => SIZES[props.size || 'main']

  return (
    <AppTooltip
      delay={900}
      content={props.tooltip}
      contentClass={promptTooltipClass}
      className={css({ flex: '1 1 auto', minW: '0', display: 'flex' })}
    >
      <Button
        type="button"
        disabled={props.disabled || props.loading}
        onClick={props.onClick}
        style={{
          background: props.active ? 'var(--colors-editorial-active-bg)' : 'var(--colors-editorial-chip)',
          color: props.active ? 'var(--colors-editorial-active-fg)' : props.danger ? 'var(--colors-editorial-short)' : 'var(--colors-editorial-ink)',
          border: `1px solid ${props.active ? 'var(--colors-editorial-active-bg)' : 'var(--colors-editorial-hair-strong)'}`,
          gap: scale().gap,
          height: scale().h,
          'border-radius': scale().borderRadius,
          'font-size': scale().fontSize,
          '--action-btn-icon': scale().icon,
        }}
        class={css({
          pos: 'relative',
          w: 'full',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '700',
          px: '9px',
          minW: '0',
          whiteSpace: 'nowrap',
          transition: 'background .15s',
          cursor: props.disabled ? 'default' : 'pointer',
          opacity: props.disabled ? 0.5 : 1,
          '& svg': {
            width: 'var(--action-btn-icon)',
            height: 'var(--action-btn-icon)',
            flexShrink: 0,
          },
          ...(!props.active && !props.disabled ? { _hover: { bg: 'editorialHover' } } : {}),
        })}
      >
        <Show when={props.bubble && props.bubble !== '0'}>
          <Box
            style={{
              position: 'absolute',
              top: scale().badgeOffset,
              right: scale().badgeOffset,
              'min-width': scale().badge,
              height: scale().badge,
            }}
            px="5px"
            borderRadius="999px"
            fontSize="10px"
            fontWeight="700"
            display="flex"
            alignItems="center"
            justifyContent="center"
            bg="editorialAccent"
            color="editorialBadgeFg"
            boxShadow="0 0 0 2px var(--colors-editorial-chip)"
          >
            {props.bubble}
          </Box>
        </Show>
        {props.icon}
        <Box
          display={props.size === 'mobile' || props.size === 'mobileAdmin' ? 'inline' : { base: 'inline', smDown: 'none' }}
          truncate
        >
          {props.heading}
        </Box>
      </Button>
    </AppTooltip>
  )
}
